<template>
  <div class="max-w-[90vw] mx-auto">
    <div class="w-full flex justify-end align-center">
      <v-container class="onest-font">
        <a
          @click.prevent="goBack"
          style="color: inherit; cursor: pointer; text-decoration: none"
          class="d-flex align-center d-inline-flex"
        >
          <v-icon size="20" class="mr-1">mdi-arrow-u-left-bottom</v-icon>
          <span style="text-decoration: underline">voltar</span>
        </a>
        <h1 class="font-bold mt-2 text-5xl">
          {{ isEditing ? 'Editar Roteiro' : 'Novo Roteiro' }}
        </h1>
        <div class="text-lg mt-2 flex flex-col">
          <span v-if="projeto" class="font-bold">
            Projeto: <span class="font-medium">{{ projeto.nome }}</span>
          </span>
          <span v-if="projeto" class="font-bold">
            Validação:
            <span class="font-medium">{{ projeto.validacao }}</span>
          </span>
        </div>
      </v-container>
      <v-container class="onest-font d-flex justify-end gap-2">
        <v-btn
          variant="flat"
          prepend-icon="mdi-check"
          color="primary"
          @click="saveRoteiro"
          :loading="isLoading"
          :disabled="isLoading"
        >
          Salvar Roteiro
        </v-btn>
      </v-container>
    </div>
    <div v-if="isLoading" class="py-12 text-center text-gray-500 font-medium">
      <v-progress-circular
        indeterminate
        color="primary"
        class="mb-4"
        size="64"
      ></v-progress-circular>
      <p>Carregando dados do roteiro...</p>
    </div>
    <v-container v-else>
      <div>
        <h2 class="my-4 font-bold text-2xl">Informações gerais</h2>
        <div class="mt-4 flex justify-between items-center w-full gap-4">
          <div class="w-1/2">
            <p class="text-base mb-2 font-semibold">Ator Relacionado</p>
            <v-select
              placeholder="Ex. Administrador"
              v-model="ator"
              :items="atoresProjeto"
              item-title="nome_ator"
              item-value="id_ator"
              variant="solo"
              bg-color="white"
              return-object
              class="w-full"
              :disabled="isLoading"
            ></v-select>
          </div>
          <div class="w-1/2">
            <p class="text-base mb-2 font-semibold">Link de Acesso</p>
            <v-text-field
              v-model="linkAcesso"
              placeholder="Ex: https://meusistema.com/"
              variant="solo"
              bg-color="white"
              :disabled="isLoading"
            ></v-text-field>
          </div>
        </div>
      </div>
      <div class="flex gap-4 justify-between">
        <div class="w-1/2 my-4">
          <h2 class="my-5 font-bold text-2xl">Funcionalidades a testar</h2>

          <div
            v-if="!ator"
            class="text-gray-500 mb-4 bg-gray-50 p-4 rounded-lg border border-gray-200"
          >
            Selecione um Ator Relacionado para visualizar as funcionalidades a
            testar.
          </div>
          <div
            v-else-if="funcionalidadesFiltradas.length === 0"
            class="text-gray-500 mb-4 bg-gray-50 p-4 rounded-lg border border-gray-200"
          >
            Nenhuma funcionalidade associada aos casos de uso deste ator na
            validação atual.
          </div>

          <div
            v-else
            v-for="item in funcionalidadesFiltradas"
            :key="item.id"
            class="bg-white border border-gray-300 rounded-lg mb-4 transition-all"
            :class="{ 'border-l-4 border-l-[#004984]': item.selecionado }"
          >
            <!-- Cabeçalho -->
            <div
              class="p-4 flex justify-between items-center"
              :class="
                isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
              "
              @click="!isLoading && (item.selecionado = !item.selecionado)"
            >
              <div class="flex items-center gap-3">
                <v-icon
                  :color="item.selecionado ? 'primary' : 'grey-lighten-1'"
                >
                  {{
                    item.selecionado
                      ? 'mdi-checkbox-marked'
                      : 'mdi-checkbox-blank-outline'
                  }}
                </v-icon>
                <span class="font-semibold text-lg">{{ item.nome }}</span>
              </div>
              <div class="status" :class="{ ativo: item.selecionado }" />
            </div>

            <!-- Corpo do detalhamento (visível apenas se selecionado) -->
            <v-expand-transition>
              <div
                v-show="item.selecionado"
                class="px-4 pb-4 border-t border-gray-100 pt-3"
              >
                <div class="flex justify-between items-center mb-2">
                  <p class="font-medium text-gray-700">O que será testado?</p>
                </div>
                <v-textarea
                  v-model="item.oQueDeveTestar"
                  rows="3"
                  auto-grow
                  variant="solo"
                  bg-color="#F1F1F1"
                  hide-details
                  placeholder="Ex. Busca num inventário de dados.
"
                  :disabled="isLoading"
                ></v-textarea>
              </div>
            </v-expand-transition>
          </div>
        </div>
        <div class="w-1/2 max-w-3xl my-4">
          <h2 class="my-4 font-bold text-2xl">Contas de Teste</h2>
          <div class="flex w-full mb-2">
            <p class="font-semibold text-base w-1/2">Login</p>
            <p class="font-semibold text-base w-1/2">Senha</p>
          </div>
          <div
            v-for="(conta, index) in contasTeste"
            :key="index"
            class="flex gap-4 items-center my-4 bg-gray-50 p-4 rounded-lg border border-gray-200"
          >
            <div class="flex flex-col gap-4 w-full">
              <div class="flex gap-4">
                <div class="flex flex-col gap-2 w-1/2">
                  <v-text-field
                    label="Login"
                    v-model="conta.login"
                    type="text"
                    placeholder="Ex: testador@email.com"
                    variant="solo"
                    bg-color="white"
                    hide-details
                    :disabled="isLoading"
                  />
                </div>

                <div class="flex flex-col gap-2 w-1/2">
                  <v-text-field
                    label="Senha"
                    v-model="conta.senha"
                    type="text"
                    placeholder="Defina a senha"
                    variant="solo"
                    bg-color="white"
                    hide-details
                    :disabled="isLoading"
                  />
                </div>
              </div>
              <div class="flex flex-col gap-2 w-full">
                <v-textarea
                  label="Dados de Teste (Instruções ou Anotações Opcionais)"
                  v-model="conta.dadosTeste"
                  placeholder="Ex: testador 1 acessa os dados de maria"
                  variant="solo"
                  bg-color="white"
                  hide-details
                  rows="2"
                  auto-grow
                  :disabled="isLoading"
                />
              </div>
            </div>

            <v-btn
              v-if="index === contasTeste.length - 1"
              variant="flat"
              icon="mdi-plus"
              color="primary"
              size="40"
              class="rounded-full bg-[#004984] text-white cursor-pointer"
              @click="adicionarConta"
              :disabled="isLoading"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
            <v-btn
              v-else
              variant="flat"
              icon="mdi-minus"
              color="error"
              size="40"
              class="rounded-full cursor-pointer"
              @click="removerConta(index)"
              :disabled="isLoading"
            >
              <v-icon>mdi-minus</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
/**
 * @module NovoRoteiro
 * @description Componente que permite criar um novo roteiro de testes
 *
 * @param {string} idProjeto - ID do projeto
 *
 * @method fetchProjetoInfo - Busca as informações do projeto com a API
 * @method fetchAtoresProjeto - Busca os atores do projeto com a API
 * @method goBack - Retorna para a página anterior
 * @method saveRoteiro - Salva o roteiro com a API
 * @method adicionarConta - Adiciona uma nova conta de teste
 * @method removerConta - Remove uma conta de teste
 */
import axios from 'axios';

import store from '@/store/store';
const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3000';
export default {
  data() {
    return {
      isLoading: false,
      isEditing: false,
      idRoteiro: null,
      searchQuery: '',
      projeto: { nome: 'Carregando...', validacao: 'Carregando...' },
      idProjeto: '',
      roteiros: [],
      ator: null,
      atoresProjeto: [],
      funcionalidades: [
        { nome: 'Carregando...', selecionado: false, atoresPermitidos: [] },
      ],
      contasTeste: [{ login: '', senha: '', dadosTeste: '' }],
      linkAcesso: '',
      idValidacao: null,
    };
  },
  computed: {
    funcionalidadesFiltradas() {
      if (!this.ator) {
        return [];
      }

      return this.funcionalidades.filter(
        (f) =>
          f.atoresPermitidos &&
          f.atoresPermitidos.includes(Number(this.ator.id_ator)),
      );
    },
    funcionalidadesSelecionadas() {
      return this.funcionalidadesFiltradas.filter((item) => item.selecionado);
    },
  },
  async mounted() {
    this.isLoading = true;
    try {
      this.idProjeto = this.$route.params.idProjeto;
      this.idRoteiro = this.$route.params.idRoteiro;
      if (this.idRoteiro) {
        this.isEditing = true;
      }
      if (this.idProjeto) {
        await Promise.all([this.fetchAtoresProjeto(), this.fetchProjetoInfo()]);
      }
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    /**
     * @method Busca as informações do projeto com a API
     */
    fetchProjetoInfo() {
      return axios
        .get(`${apiUrl}/projetos/listaprojeto/${this.idProjeto}`, {
          headers: {
            Authorization: `Bearer ${store.state.token}`,
          },
        })
        .then((res) => {
          if (res.data) {
            this.projeto.nome = res.data.nome || 'Não foi possível carregar';

            // Pega a validação mais recente através da tabela associativa
            const associacao = res.data.tb_projetos_envolvidos?.[0];
            const validacao = associacao?.tb_validacao;

            this.projeto.validacao =
              validacao?.nome_validacao || 'Não foi possível carregar';

            // Salva o id_validacao para consultar os roteiros específicos
            if (validacao) {
              this.idValidacao = validacao.id_validacao;

              return this.fetchFuncionalidades();
            }
          }
        })
        .catch((error) => {
          console.error('Erro ao buscar dados do projeto:', error);
          this.$toast.error('Erro ao buscar informações do projeto.');
        });
    },
    /**
     * @method Busca os atores do projeto com a API
     *
     */
    fetchAtoresProjeto() {
      return axios
        .get(`${apiUrl}/api/v1/ator/projeto/${this.idProjeto}`, {
          headers: {
            Authorization: `Bearer ${store.state.token}`,
          },
        })
        .then((res) => {
          this.atoresProjeto = res.data.ator || [];
        })
        .catch((error) => {
          console.error('Erro ao buscar atores:', error);
          this.$toast.error('Erro ao carregar atores do projeto.');
        });
    },

    /**
     * @method Busca as funcionalidades do projeto com a API
     */
    fetchFuncionalidades() {
      if (!this.idValidacao) {
        return Promise.resolve();
      }

      return axios
        .get(
          `${apiUrl}/api/v1/projetos/${this.idProjeto}/escopos?id_validacao=${this.idValidacao}`,
          {
            headers: {
              Authorization: `Bearer ${store.state.token}`,
            },
          },
        )
        .then((res) => {
          this.funcionalidades = Array.isArray(res.data)
            ? res.data.map((e) => ({
                id: e.id_escopo,
                nome: e.funcionalidade,
                selecionado: false,
                oQueDeveTestar: '',
                atoresPermitidos:
                  e.tb_caso_de_uso?.tb_caso_de_uso_ator?.map((a) =>
                    Number(a.id_ator),
                  ) || [],
              }))
            : [];
          if (this.isEditing) {
            return this.loadRoteiroDetails();
          }
        })
        .catch((error) => {
          console.error('Erro ao buscar funcionalidades:', error);
          this.$toast.error('Erro ao carregar funcionalidades do projeto.');
        });
    },

    /**
     * @method Carrega os detalhes do roteiro por consulta a API
     */
    loadRoteiroDetails() {
      return axios
        .get(`${apiUrl}/api/v1/roteiros/${this.idRoteiro}`, {
          headers: {
            Authorization: `Bearer ${store.state.token}`,
          },
        })
        .then((res) => {
          const dados = res.data;
          this.linkAcesso = dados.link_acesso || '';

          if (dados.id_ator && this.atoresProjeto) {
            this.ator =
              this.atoresProjeto.find(
                (a) => Number(a.id_ator) === Number(dados.id_ator),
              ) || null;
          }

          if (dados.tb_conta_teste && dados.tb_conta_teste.length > 0) {
            this.contasTeste = dados.tb_conta_teste.map((c) => ({
              login: c.usuario_acesso,
              senha: c.senha_acesso,
              dadosTeste: c.dados_teste || '',
            }));
          } else {
            this.contasTeste = [{ login: '', senha: '', dadosTeste: '' }]; // Se não tiver contas cadastradas, inicia com um campo vazio
          }

          if (dados.tb_escopo_teste) {
            const escoposCarregados = dados.tb_escopo_teste;
            this.funcionalidades.forEach((f) => {
              const escopo = escoposCarregados.find(
                (e) => Number(e.id_escopo) === Number(f.id),
              );
              if (escopo) {
                f.selecionado = true;
                f.oQueDeveTestar = escopo.o_que_deve_testar || '';
              }
            });
          }
        })
        .catch((error) => {
          console.error('Erro ao carregar os dados do roteiro:', error);
          this.$toast.error('Erro ao buscar as informações do roteiro.');
        });
    },

    /**
     * @method Salva ou edita o roteiro no banco de dados pela API
     */
    saveRoteiro() {
      // Validar dados básicos
      if (!this.ator) {
        this.$toast.error('Selecione um ator responsável pelo teste.');

        return;
      }
      if (this.funcionalidadesSelecionadas.length === 0) {
        this.$toast.error('Selecione pelo menos uma funcionalidade.');

        return;
      }

      // Filtrar contas que foram preenchidas (ignorar as vazias)
      const contasValidas = this.contasTeste
        .filter((c) => c.login.trim() !== '' && c.senha.trim() !== '')
        .map((c) => ({
          login: c.login,
          senha: c.senha,
          // eslint-disable-next-line camelcase
          dados_teste: c.dadosTeste,
        }));

      // Filtrar funcionalidades com detalhamento
      const funcionalidadesValidas = this.funcionalidadesSelecionadas.map(
        (f) => ({
          idEscopo: f.id,
          oQueDeveTestar: f.oQueDeveTestar || 'Sem detalhamento específico',
        }),
      );

      const payload = {
        linkAcesso: this.linkAcesso,
        idAtor: this.ator.id_ator,
        idValidacao: this.idValidacao,
        contasTeste: contasValidas,
        funcionalidades: funcionalidadesValidas,
      };

      const request = this.isEditing
        ? axios.put(`${apiUrl}/api/v1/roteiros/${this.idRoteiro}`, payload, {
            headers: { Authorization: `Bearer ${store.state.token}` },
          })
        : axios.post(
            `${apiUrl}/api/v1/projetos/${this.idProjeto}/roteiros`,
            payload,
            {
              headers: { Authorization: `Bearer ${store.state.token}` },
            },
          );

      request
        .then(() => {
          this.$toast.success(
            `Roteiro ${this.isEditing ? 'atualizado' : 'criado'} com sucesso!`,
          );
          this.$router.push(`/projects/${this.idProjeto}/roteiros/`);
        })
        .catch((err) => {
          console.error('Erro ao salvar o roteiro:', err);
          if (err.response && err.response.data) {
            console.error('Detalhes do erro (API):', err.response.data);
            this.$toast.error(
              err.response.data.error || 'Erro ao salvar o roteiro.',
            );
          } else {
            this.$toast.error('Erro ao salvar o roteiro.');
          }
        });
    },
    goBack() {
      this.$router.push(`/projects/${this.idProjeto}/roteiros/`);
    },
    adicionarConta() {
      this.contasTeste.push({ login: '', senha: '', dadosTeste: '' });
    },
    removerConta(index) {
      this.contasTeste.splice(index, 1);
    },
  },
};
</script>
